// Props standing in the world (docs/plans/2026-10-17-asset-phases.md): one sprite from the atlas (tools/make-sprites.py)
// at a map point, depth-sorted with the trees, buildings and bodies. Mode code makes them with standing() and passes
// them to TreeLayer.draw(…, extra).
import { Standing } from './render';
import { SCALE as BODY } from './people';
import { CAR_SCALE } from './traffic';
import SPRITES from './sprites.json';

interface Sprite { x: number; y: number; w: number; h: number; ax: number; ay: number; r: number; ppm?: number }

/**
 * Props come in two sizes at toy scale. body: anything in a body's hands or at its feet, drawn as the people are, so a
 * 1.1 m barricade comes up to a body's hip. structure: things bodies walk around or into, drawn as the cars are (at the
 * body's scale a metro gate would be 13 m wide).
 */
export const TIERS = { body: BODY, structure: CAR_SCALE } as const;
export type Tier = keyof typeof TIERS;

/** Each prop's tier, by its sprite key without the '@turn' and '~colour' suffixes. Unlisted keys are body props. */
export const PROP_TIER: Record<string, Tier> = {
  metro_gate_open: 'structure', metro_gate_shut: 'structure', police_booth: 'structure', cctv_pole: 'structure',
  surveillance_van: 'structure', auto_rickshaw: 'structure', auto_rickshaw_black: 'structure',
  'quaternius-public-transport/Bus': 'structure', 'oga-concert-props/stage-005': 'structure',
};

/** 'pack/model@90~F1ECE0' -> 'pack/model' */
export const stemOf = (key: string) => key.split(/[@~]/)[0];
export const tierOf = (key: string): Tier => PROP_TIER[stemOf(key)] || 'body';

const sprites = SPRITES.sprites as Record<string, Sprite>;
let img: HTMLImageElement | null = null, loading = false;
/** The sprite atlas, once it has loaded (null until then; starts loading on the first call). */
export function atlas(): HTMLImageElement | null {
  if (!img && !loading && typeof Image !== 'undefined') {
    loading = true;
    const i = new Image();
    i.onload = () => { img = i; };
    i.src = SPRITES.image;
  }
  return img;
}

/**
 * The prop `key` (a sprites.json key) with its foot at (x, y), world px, where a metre is `metre` world px; null if the
 * atlas has no such sprite. Draws nothing until the atlas has loaded. `tier` is a tier's name, or the scale itself (how
 * many times life size), for props fitted to something else, such as a wall to a tile edge.
 */
export function standing(key: string, x: number, y: number, metre: number, tier: Tier | number = tierOf(key), alpha = 1): Standing | null {
  const s = sprites[key];
  if (!s) return null;
  const f = ((typeof tier === 'number' ? tier : TIERS[tier]) * metre) / (s.ppm || SPRITES.ppm);
  return {
    y,
    draw(g) {
      const im = atlas();
      if (!im) return;
      g.globalAlpha = alpha;
      g.drawImage(im, s.x, s.y, s.w, s.h, x - s.ax * f, y - s.ay * f, s.w * f, s.h * f);
      g.globalAlpha = 1;
    },
  };
}

/** Of the four turns a barrier sprite comes in, the one closest to a line at `deg` degrees (screen, y down). */
export function turnFor(deg: number): 0 | 45 | 90 | 135 {
  return ((Math.round((((-deg % 180) + 180) % 180) / 45) % 4) * 45) as 0 | 45 | 90 | 135;
}
